import { SearchService } from '@/scripts/libs/search'
import { SuggestedProducts } from '@/types/search'
import { AlgoliaContentHit } from '@/scripts/libs/algolia/types/search'
import { AlgoliaProductHit, AlgoliaSuggestion } from '@/scripts/libs/algolia/types/plp'
import Algolia, { SearchResponse } from '@/scripts/libs/algolia'
import AlgoliaPlp from '@/scripts/libs/algolia/plp'
import AlgoliaProductsUtils from '@/scripts/libs/algolia/products'
import AlgoliaFacetsUtils from '@/scripts/libs/algolia/facets'
import wcs from '@/scripts/libs/wcs'
import { WcsLocales } from '@/scripts/libs/wcs/types/locales'
import { NoEcommerce } from '@/scripts/libs/no-ecommerce'

class AlgoliaSearch implements SearchService {
  fetchSuggestedProducts = async (searchValue: string): Promise<SuggestedProducts> => {
    await AlgoliaPlp.init()
    const { algolia } = wcs.getStoreConfig()
    const canAddContentsIndex = !NoEcommerce.isStoreNoEcommerce() && !wcs.isKorea()

    const algoliaSearchConfig = [
      {
        indexName: algolia.groupedIndex,
        params: {
          query: searchValue,
          ruleContexts: AlgoliaPlp.getRuleContexts(),
          clickAnalytics: true,
          hitsPerPage: 24,
          attributesToRetrieve: ['*'],
          facets: ['attributes_translated.*', 'sort*'],
        },
      },
      {
        indexName: `${algolia.groupedIndex}_query_suggestions`,
        params: {
          query: searchValue,
          ruleContexts: AlgoliaPlp.getRuleContexts(),
          hitsPerPage: 6,
        },
      },
    ]

    if (canAddContentsIndex) {
      algoliaSearchConfig.push({
        indexName: algolia.contentsIndex,
        params: {
          query: searchValue,
          ruleContexts: AlgoliaPlp.getRuleContexts(),
          hitsPerPage: 6,
        },
      })
    }

    const productsAndSuggestionsResults = await Algolia.multipleSearch(algoliaSearchConfig)

    const productsResult = productsAndSuggestionsResults
      .results[0] as SearchResponse<AlgoliaProductHit>
    const suggestionsResult = productsAndSuggestionsResults
      .results[1] as SearchResponse<AlgoliaSuggestion>

    const contentsResult = productsAndSuggestionsResults
      .results[2] as SearchResponse<AlgoliaContentHit>

    if ((productsResult?.renderingContent as any)?.redirect?.url) {
      const redirectUrl = (productsResult?.renderingContent as any).redirect.url
      localStorage.setItem('algoliaRedirect', redirectUrl)
    }

    const { products, facets } = await this.getProductsAndFacets({ productsResult, searchValue })

    const productsSuggestions = suggestionsResult?.hits.map(
      (hit) => hit._highlightResult?.query?.value ?? ''
    )

    const { locale } = wcs.getStoreConfig()
    const currentStore = WcsLocales.mapCurrentStore(locale)

    const links = contentsResult?.hits.map((hit) => {
      return {
        title: hit.title,
        url: `/${currentStore}${hit.url}`,
      }
    })

    return {
      products,
      facets,
      suggestions: [...productsSuggestions].slice(0, 6),
      links: links ? [...links] : [],
      numberOfProducts: productsResult.nbHits,
    }
  }

  private async getProductsAndFacets({
    searchValue,
    productsResult,
  }: {
    searchValue: string
    productsResult: SearchResponse<AlgoliaProductHit>
  }) {
    if (productsResult.hits?.length === 0) {
      return {
        products: [],
        facets: undefined,
      }
    }

    const productsQueries = productsResult.hits.map((hit) => ({
      query: searchValue,
      ruleContexts: AlgoliaPlp.getRuleContexts(),
      facetFilters: [`x_groupkey: ${hit.x_groupkey}`],
      clickAnalytics: true,
    }))

    const ungroupedSearchResult = await Algolia.ungroupedSearch<AlgoliaProductHit>({
      queries: productsQueries,
    })

    const groupedObjectIDs = productsResult.hits.map((product) => product.objectID)

    const products = productsResult.hits
      .filter((product) => !!product.attributes)
      .map((hit, index) => {
        const carouselOrder = hit?.variants?.length
          ? hit.variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
          : [{ productPartNumber: hit.parentPartnumberId }]

        const product = {
          ...hit,
          variants: ungroupedSearchResult.results?.find((ungroupedProduct) =>
            ungroupedProduct.hits.some((ungroupedHit) => ungroupedHit.x_groupkey === hit.x_groupkey)
          )?.hits,
        }
        const ungroupedObjectIDs = product.variants?.map((variant) => variant.objectID)
        const result = AlgoliaProductsUtils.normalizeProduct({
          product: product,
          algoliaTracking: {
            grouped: {
              queryID: productsResult.queryID,
              indexName: Algolia.getGroupedIndexNameOrDefault(),
              objectIDs: groupedObjectIDs,
              position: index,
            },
            ungrouped: {
              queryID: productsResult.queryID,
              indexName: Algolia.getUngroupedIndexNameOrDefault(),
              objectIDs: ungroupedObjectIDs,
            },
            carouselOrder,
          },
        })
        return result
      })

    const facets = await AlgoliaFacetsUtils.getFacets({
      searchResponse: productsResult,
    })

    return {
      products,
      facets,
    }
  }
}

export default new AlgoliaSearch()
